import axios from 'axios';
import { DEVELOPMENT_CONFIG, logDevelopmentConfig } from './development';

// Determine the base URL based on environment
// Updated to fix double /api path issue - Railway backend is at /api
const getBaseURL = () => {
  // Log configuration in development
  if (process.env.NODE_ENV === 'development') {
    logDevelopmentConfig();
  }
  
  let baseUrl;
  
  if (process.env.NODE_ENV === 'production') {
    // In production, always use the Railway backend
    baseUrl = 'https://trip-tribe-app-production.up.railway.app';
    console.log('🚀 Production environment - using Railway backend:', baseUrl);     
    
    // Debug logging for production
    console.log('🔍 DEBUG - NODE_ENV:', process.env.NODE_ENV);
    console.log('🔍 DEBUG - REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('🔍 DEBUG - baseUrl before processing:', baseUrl);
  } else {
    // In development, use the development config
    baseUrl = DEVELOPMENT_CONFIG.getApiUrl();
  }
  
  // Add /api to the base URL if it's not already there
  const finalUrl = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
  
  // Debug logging for both environments
  console.log('🔍 API Configuration:');
  console.log('  Environment:', process.env.NODE_ENV);
  console.log('  Base URL:', baseUrl);
  console.log('  Final URL:', finalUrl);
  
  return finalUrl;
};

/**
 * Admin calls must use the traveler/admin JWT. `/admin/partners/:id/plan`
 * contains `/partners/`, which would otherwise prefer a partner session and
 * fail the admin email check.
 */
export function selectRequestAuthToken(path, partnerToken, userToken) {
  const normalized = String(path || '');
  if (normalized.includes('/admin')) {
    return userToken || null;
  }

  const partnerScoped = /\/partner(?:-|$|\/)/.test(normalized)
    || normalized.includes('/partner-packages')
    || normalized.includes('/partner-users')
    || normalized.includes('/partner-commission')
    || normalized.includes('/partners/');

  return partnerScoped
    ? (partnerToken || userToken)
    : (userToken || partnerToken);
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const segment = token.split('.')[1];
  if (!segment) return null;
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return JSON.parse(atob(padded));
}

export function storeRenewedSessionToken(renewedToken) {
  if (!renewedToken) return;
  try {
    const payload = decodeJwtPayload(renewedToken);
    if (payload?.type === 'partner' || payload?.type === 'partner_user') {
      localStorage.setItem('partnerToken', renewedToken);
      return;
    }
  } catch (error) {
    // Fall through and store as a traveler token.
  }
  localStorage.setItem('token', renewedToken);
}

export const SESSION_AUTH_ERROR_MESSAGES = [
  'Invalid token',
  'No token provided',
  'Token expired',
  'Invalid or expired token',
  'Session revoked. Please log in again.',
];

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: true,
  timeout: 30000 // 30 second timeout (needed for Stripe + Duffel booking in production)
});

// Add a request interceptor to include the auth token (user or partner)
api.interceptors.request.use(
  (config) => {
    const partnerToken = localStorage.getItem('partnerToken');
    const userToken = localStorage.getItem('token');
    const token = config.usePartnerAuth && partnerToken
      ? partnerToken
      : selectRequestAuthToken(config.url, partnerToken, userToken);

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const guestToken = localStorage.getItem('memoriesGuestToken');
    if (guestToken) {
      config.headers['X-Guest-Token'] = guestToken;
    }
    // Let the browser set multipart boundary for FormData uploads
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiry and silent renewal
api.interceptors.response.use(
  (response) => {
    // Sliding session: if the backend issued a renewed token, save it silently
    const renewedToken = response.headers['x-renewed-token'];
    if (renewedToken) {
      storeRenewedSessionToken(renewedToken);
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.response?.config?.url || '';

    // Never log the user out for public endpoints or booking endpoints
    const isPublicEndpoint = (
      url.includes('/duffel/') ||
      url.includes('/events/') ||
      url.includes('/packages') ||
      url.includes('/places/') ||
      url.includes('/payments/') || // Don't auto-redirect for payment errors
      url.includes('/newsletter/') || // Newsletter is public
      url.includes('/partners/interest') || // Partner interest list is public
      url.includes('/partners/demo-request') || // Partner demo intake is public
      url.includes('/partners/demo-booked') || // Partner demo Calendly confirmation is public
      url.includes('/campaigns/visit') ||
      url.includes('/campaigns/booked') ||
      url.includes('/forum/') || // Forum browsing is public
      url.includes('/memories/invites/')
    );

    if (!isPublicEndpoint && (status === 401 || status === 403)) {
      // Check if the error is related to token issues
      const errorMessage = error.response?.data?.error;
      if (SESSION_AUTH_ERROR_MESSAGES.includes(errorMessage)) {
        // Check if partner or user is logged in
        const partnerToken = localStorage.getItem('partnerToken');
        const userToken = localStorage.getItem('token');
        
        if (partnerToken) {
          // Clear partner authentication data
          localStorage.removeItem('partnerToken');
          localStorage.removeItem('partner');
          
          // Only redirect if we're not already on the partner login page
          if (window.location.pathname !== '/partner/login') {
            window.location.href = '/partner/login';
          }
        } else if (userToken) {
          // Clear user authentication data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Dispatch a custom event to notify AuthContext
          window.dispatchEvent(new CustomEvent('auth:token-expired'));
          
          // Only redirect if we're not already on the login page
          if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api; 